import { ENV } from '../../../config';

export interface CalcJob {
  id: string;
  standardCalculationId: string | null;
  calcName: string;
  configName: string | null;
  type: 'Period' | 'Event' | 'Operational' | string;
  sequence: number;
  status: 'Completed' | 'Running' | 'InProgress' | 'Pending' | 'Queued' | 'Failed' | 'Error';
  resultSummary: string | null;
  completedAt: string | null;
  errorMessage?: string | null;
}

export interface ProjectExecution {
  id: string | number;
  name: string;
  status: string;
  taxYear: string;
  partnershipName: string;
  engagementName: string | null;
  progressPct: number;
  totalCalcs: number;
  completedCalcs: number;
  inProgressCalcs?: number;
  pendingCalcs?: number;
  jobs: CalcJob[];
}

export interface CalcResultSummary {
  jobId: string;
  calcName: string;
  standardCalculationId: string | null;
  outputRows: number;
  keyMetric: string | null;
  resultSummary: string | null;
}

export interface CalculationResult {
  id: string;
  jobId: string;
  entityId: number;
  partnerId: number;
  taxYear: number;
  lineCode: string;
  lineName: string;
  amount: number;
  bookType: string;
  createdAt: string;
}

const BASE = `${ENV.API_URL}/api`;

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    tenant: ENV.TENANT_ID ?? '',
  };
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw Object.assign(new Error('API error'), { status: r.status, data: err });
  }
  return r.json();
}

export interface ProjectSummary {
  id: number;
  name: string;
  status: string;
  taxYear?: number;
  dateCreated?: string;
}

export interface CalculationJob {
  id: string | number;
  status: 'Completed' | 'Running' | 'InProgress' | 'Pending' | 'Queued' | 'Failed' | 'Error' | 'Blocked';
  entityId?: number;
  errorMessage?: string | null;
}

export interface ProjectReadinessCheck {
  name: string;
  status: 'pass' | 'fail' | 'warn';
  detail: string;
}

export interface ProjectReadiness {
  projectId: number;
  isReadyToRun: boolean;
  checks: ProjectReadinessCheck[];
}

export const projectService = {
  getByPartnership(partnershipId: string | number, taxYear: string): Promise<ProjectExecution> {
    return apiFetch<ProjectExecution>(
      `/project/by-partnership?partnershipId=${partnershipId}&taxYear=${encodeURIComponent(taxYear)}`,
    );
  },

  listByPartnership(partnershipId: string | number, taxYear: string): Promise<ProjectSummary[]> {
    const taxYearNum = parseInt(taxYear.replace(/^FY/i, ''), 10);
    return apiFetch<{ totalCount: number; items: ProjectSummary[] }>('/project/list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityId: Number(partnershipId),
        taxYear: isNaN(taxYearNum) ? undefined : taxYearNum,
        itemsPerPage: 50,
        startIndex: 0,
      }),
    }).then(r => r.items ?? []);
  },

  runNext(projectId: string | number): Promise<Record<string, unknown>[]> {
    return apiFetch(`/project/${projectId}/run`, { method: 'POST' });
  },

  runProject(projectId: string | number): Promise<CalculationJob[]> {
    return apiFetch<CalculationJob[]>(`/calculations/run/${projectId}`, { method: 'POST' });
  },

  getJobs(projectId: string | number): Promise<CalculationJob[]> {
    return apiFetch<CalculationJob[]>(`/calculations/jobs/${projectId}`);
  },

  getStatus(projectId: string | number): Promise<ProjectExecution> {
    return apiFetch<ProjectExecution>(`/project/${projectId}/status`);
  },

  getResults(projectId: string | number): Promise<CalcResultSummary[]> {
    return apiFetch<CalcResultSummary[]>(`/project/${projectId}/results`);
  },

  getJobResults(jobId: string): Promise<CalculationResult[]> {
    return apiFetch<CalculationResult[]>(`/calculations/jobs/${jobId}/results`);
  },

  getReadiness(projectId: string | number): Promise<ProjectReadiness> {
    return apiFetch<ProjectReadiness>(`/project/${projectId}/readiness`);
  },

  archiveProject(projectId: string | number): Promise<void> {
    return fetch(`${BASE}/project/${projectId}`, { method: 'DELETE', headers: authHeaders() })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw Object.assign(new Error('API error'), { status: r.status, data: err });
        }
      });
  },

  downloadK1Pdf(projectId: string | number, entityId: number): Promise<Blob> {
    return fetch(`${BASE}/k1/${projectId}/partner/${entityId}/pdf`, { headers: authHeaders() })
      .then(r => r.blob());
  },

  exportGoSystemXml(projectId: string | number): Promise<Blob> {
    return fetch(`${BASE}/project/${projectId}/export/gosystem-xml`, { headers: authHeaders() })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw Object.assign(new Error('API error'), { status: r.status, data: err });
        }
        return r.blob();
      });
  },

  exportWorkPapers(projectId: string | number): Promise<Blob> {
    return fetch(`${BASE}/project/${projectId}/export/workpapers`, { headers: authHeaders() })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw Object.assign(new Error('API error'), { status: r.status, data: err });
        }
        return r.blob();
      });
  },
};
